import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  Alert,
} from 'react-native';

import { FileText, FileDown, ChevronDown } from './../icons';
import { useRackConfigurations } from '../../hooks/queries/useRackConfigurations';
import { useAttachments } from '../../hooks/useAttachments';
import {
  isInlineAttachment,
  isPdfEmbeddableImageAttachment,
} from '../../services/attachmentService';
import {
  generateBlankPDF,
  generateFilledPDF,
  downloadPDF,
  DOCX_MIME,
  PdfImageOptions,
} from '../../services/pdfService';
import { spacing } from '../../theme/spacing';
import {
  ImageSelectionModal,
  ImageSelectionResult,
} from '../modals/ImageSelectionModal';
import { PDFViewerModal, PDFSource } from '../modals/PDFViewerModal';

import type { DocumentFormat } from '../../types/pdf';

interface PrintPDFDropdownProps {
  formData?: any; // Optional form data for filled PDF
  formDataRef?: React.RefObject<any>;
  disabled?: boolean;
}

export const PrintPDFDropdown: React.FC<PrintPDFDropdownProps> = ({
  formData,
  formDataRef,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
  const [pdfSource, setPdfSource] = useState<PDFSource | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string>('document.pdf');
  // #1340: image-selection step shown before generating a Summary PDF.
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [pendingPdfFormData, setPendingPdfFormData] = useState<any>(null);
  const buttonRef = useRef<TouchableOpacity>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Live racks for the filled PDF. formData (the request) carries a
  // rackConfigurations snapshot that goes stale after a rack is saved (rack CRUD
  // uses the rack API, not the request query), so the PDF missed just-saved racks
  // until a full refresh. Merge the live, save-invalidated rack query instead.
  const liveFormData = formDataRef?.current ?? formData;
  const pdfRequestId = liveFormData?.id != null ? String(liveFormData.id) : '';
  const racksEnabled =
    pdfRequestId !== '' &&
    pdfRequestId !== 'new-request' &&
    !pdfRequestId.startsWith('o-');
  const { refetch: refetchRacks } = useRackConfigurations(
    pdfRequestId,
    racksEnabled,
  );

  // #1340: image attachments available for embedding. Only fetch for a real,
  // saved request id (not new/offline), matching the racks gate.
  const { attachments, refetch: refetchAttachments } = useAttachments(
    racksEnabled ? pdfRequestId : null,
  );
  // Uploaded photos only. Inline rich-text images print in the field, not here.
  const imageAttachments = attachments.filter(
    attachment =>
      !isInlineAttachment(attachment) &&
      isPdfEmbeddableImageAttachment(attachment),
  );

  const resolveFormData = () => formDataRef?.current ?? formData;

  // Turn generated PDF bytes into a viewer source and open the PDF viewer.
  const showGeneratedPdf = (pdfData: Blob | string, filename: string) => {
    let source: PDFSource;
    if (Platform.OS === 'web' && pdfData instanceof Blob) {
      source = {
        type: 'blob',
        data: pdfData,
        mimeType: 'application/pdf',
        fileName: filename,
      };
    } else if (typeof pdfData === 'string') {
      // Data URL or base64
      source = {
        type: pdfData.startsWith('data:') ? 'dataUrl' : 'base64',
        data: pdfData,
        mimeType: 'application/pdf',
        fileName: filename,
      };
    } else {
      throw new Error('Unexpected PDF data format');
    }

    setPdfSource(source);
    setPdfFileName(filename);
    setPdfViewerVisible(true);
  };

  // Present the generated document. PDFs open in the in-app viewer; Word (.docx)
  // can't be previewed by react-pdf / react-native-pdf, so it downloads (#1390).
  const presentDocument = async (
    data: Blob | string,
    filename: string,
    format: DocumentFormat,
  ) => {
    if (format === 'docx') {
      await downloadPDF(data, filename, DOCX_MIME);
      return;
    }
    showGeneratedPdf(data, filename);
  };

  // Generate the filled (Summary) document and present it. Optional imageOptions
  // embeds the user-selected image attachments (#1340); format picks PDF or Word
  // (#1390) — embedded images apply to both since they share the DOCX render path.
  const generateSummary = async (
    pdfFormData: any,
    imageOptions?: PdfImageOptions,
    format: DocumentFormat = 'pdf',
  ) => {
    setIsLoading(true);
    try {
      const generated = await generateFilledPDF(
        pdfFormData,
        imageOptions,
        format,
      );
      await presentDocument(generated.data, generated.fileName, format);
    } catch (error: any) {
      console.error('Document generation error:', error);
      Alert.alert('Error', `Failed to generate document: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Summary always opens the export popup — it hosts both image selection and the
  // PDF/Word format choice (#1340 + #1390). Even with no images, the format
  // toggle still applies.
  const handleSummarySelect = async () => {
    setIsOpen(false);

    const resolvedFormData = resolveFormData();
    if (!resolvedFormData) {
      Alert.alert('Error', 'No form data available to generate document');
      return;
    }

    setIsLoading(true);
    // Pull the latest racks so a just-saved rack is in the document without a
    // manual page refresh (#995 bug 4).
    let pdfFormData = resolvedFormData;
    if (racksEnabled) {
      try {
        const fresh = await refetchRacks();
        if (Array.isArray(fresh.data)) {
          pdfFormData = {
            ...resolvedFormData,
            rackConfigurations: fresh.data,
          };
        }
      } catch {
        // Fall back to the formData snapshot if the refetch fails.
      }
    }

    // #1421: pull the latest attachments so images saved without a page refresh appear
    // in the Export report popup. The ImageSelectionModal reads `imageAttachments`,
    // derived from this hook's state; awaiting the refetch updates that state before the
    // modal opens. Mirrors the rack refetch above.
    if (racksEnabled) {
      try {
        await refetchAttachments();
      } catch {
        // Fall back to the current attachment snapshot if the refetch fails.
      }
    }

    setPendingPdfFormData(pdfFormData);
    setIsLoading(false);
    setImageModalVisible(true);
  };

  // Blank form has no images, so it keeps a direct per-format action (#1390).
  const handleBlankSelect = async (format: DocumentFormat) => {
    setIsOpen(false);
    setIsLoading(true);
    try {
      const data = await generateBlankPDF(format);
      const ext = format === 'docx' ? 'docx' : 'pdf';
      await presentDocument(data, `VertivForm_Blank.${ext}`, format);
    } catch (error: any) {
      console.error('Document generation error:', error);
      Alert.alert('Error', `Failed to generate document: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelectionConfirm = async (result: ImageSelectionResult) => {
    setImageModalVisible(false);
    const pdfFormData = pendingPdfFormData;
    setPendingPdfFormData(null);
    if (!pdfFormData) {
      return;
    }
    const selectedIds = [...new Set(result.selectedIds)];
    const hasImageSelection = result.includeAll || selectedIds.length > 0;
    await generateSummary(
      pdfFormData,
      hasImageSelection
        ? {
            includeAll: result.includeAll,
            selectedIds,
            // The modal chooses a columns×rows grid; the backend takes a per-page
            // count plus explicit columns (to tell 1×2 stacked from 2×1
            // side-by-side).
            imagesPerPage: result.columns * result.rows,
            columns: result.columns,
          }
        : undefined,
      result.format,
    );
  };

  const handleImageSelectionCancel = () => {
    setImageModalVisible(false);
    setPendingPdfFormData(null);
  };

  const handleClosePdfViewer = () => {
    setPdfViewerVisible(false);
    setPdfSource(null);
  };

  // Positioning function for dropdown
  const updateDropdownPosition = () => {
    if (Platform.OS === 'web' && buttonRef.current) {
      const element = buttonRef.current as any;
      const rect = element.getBoundingClientRect();
      const dropdownWidth = Math.max(rect.width, 280);

      // Check if dropdown would overflow viewport on the right
      const spaceOnRight = window.innerWidth - rect.left;
      const shouldAlignRight = spaceOnRight < dropdownWidth;

      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: shouldAlignRight
          ? rect.right + window.scrollX - dropdownWidth
          : rect.left + window.scrollX,
        width: dropdownWidth,
      });
    }
  };

  // Close dropdown when clicking outside (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && isOpen && typeof document !== 'undefined') {
      const handleClickOutside = (event: any) => {
        const portal = document.querySelector(
          '[data-print-pdf-dropdown-portal="true"]',
        );
        if (
          portal &&
          !portal.contains(event.target) &&
          !buttonRef.current?.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen && Platform.OS === 'web') {
      updateDropdownPosition();
    }
  }, [isOpen]);

  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <TouchableOpacity
          ref={buttonRef}
          style={[
            styles.triggerButton,
            (disabled || isLoading) && styles.disabledButton,
          ]}
          onPress={() => !disabled && !isLoading && setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
          testID="pdf-dropdown-trigger"
        >
          <FileDown
            size={Platform.OS === 'web' ? 16 : 14}
            color={disabled || isLoading ? '#ccc' : '#fff'}
            style={styles.icon}
          />
          <Text
            style={[
              styles.buttonText,
              (disabled || isLoading) && styles.disabledText,
            ]}
          >
            {isLoading ? 'Generating...' : 'PDF'}
          </Text>
          <ChevronDown
            size={Platform.OS === 'web' ? 14 : 12}
            color={disabled || isLoading ? '#ccc' : '#fff'}
            style={styles.chevron}
          />
        </TouchableOpacity>

        {/* Portal dropdown */}
        {isOpen &&
          !disabled &&
          createPortal &&
          createPortal(
            <div
              data-print-pdf-dropdown-portal="true"
              data-testid="pdf-dropdown-menu"
              style={{
                position: 'absolute',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                zIndex: 999999,
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '8px 0' }}>
                <div
                  data-testid="pdf-option-summary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                  }}
                  onClick={() => handleSummarySelect()}
                >
                  <FileText
                    size={16}
                    color="#333"
                    style={{ marginRight: 12 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#333',
                        marginBottom: '2px',
                      }}
                    >
                      Summary
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      Filled form — choose images &amp; format
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#e0e0e0',
                    margin: '0 16px',
                  }}
                />

                <div
                  data-testid="pdf-option-blank-pdf"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                  }}
                  onClick={() => handleBlankSelect('pdf')}
                >
                  <FileText
                    size={16}
                    color="#333"
                    style={{ marginRight: 12 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#333',
                        marginBottom: '2px',
                      }}
                    >
                      Blank Form — PDF
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      Empty form for handwriting
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#e0e0e0',
                    margin: '0 16px',
                  }}
                />

                <div
                  data-testid="pdf-option-blank-word"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                  }}
                  onClick={() => handleBlankSelect('docx')}
                >
                  <FileText
                    size={16}
                    color="#333"
                    style={{ marginRight: 12 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#333',
                        marginBottom: '2px',
                      }}
                    >
                      Blank Form — Word
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                      Empty form as an editable .docx
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            typeof document !== 'undefined' ? document.body : (null as any),
          )}

        <PDFViewerModal
          visible={pdfViewerVisible}
          onClose={handleClosePdfViewer}
          source={pdfSource}
          downloadFileName={pdfFileName}
        />

        <ImageSelectionModal
          visible={imageModalVisible}
          images={imageAttachments}
          onCancel={handleImageSelectionCancel}
          onConfirm={handleImageSelectionConfirm}
        />
      </View>
    );
  }

  // React Native Modal version
  return (
    <View style={styles.container}>
      <TouchableOpacity
        ref={buttonRef}
        style={[
          styles.triggerButton,
          (disabled || isLoading) && styles.disabledButton,
        ]}
        onPress={() => !disabled && !isLoading && setIsOpen(true)}
        disabled={disabled || isLoading}
      >
        <FileDown
          size={Platform.OS === 'web' ? 16 : 14}
          color={disabled || isLoading ? '#ccc' : '#fff'}
          style={styles.icon}
        />
        <Text
          style={[
            styles.buttonText,
            (disabled || isLoading) && styles.disabledText,
          ]}
        >
          {isLoading ? 'Generating...' : 'PDF'}
        </Text>
        <ChevronDown
          size={Platform.OS === 'web' ? 14 : 12}
          color={disabled || isLoading ? '#ccc' : '#fff'}
          style={styles.chevron}
        />
      </TouchableOpacity>

      <Modal
        visible={isOpen && !disabled && !isLoading}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setIsOpen(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Export</Text>

            <TouchableOpacity
              style={styles.modalOption}
              onPress={() => handleSummarySelect()}
              testID="pdf-option-summary"
            >
              <FileText size={20} color="#333" style={styles.optionIcon} />
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>Summary</Text>
                <Text style={styles.optionDescription}>
                  Filled form — choose images & format
                </Text>
              </View>
            </TouchableOpacity>

            <View style={styles.optionSeparator} />

            <TouchableOpacity
              style={styles.modalOption}
              onPress={() => handleBlankSelect('pdf')}
              testID="pdf-option-blank-pdf"
            >
              <FileText size={20} color="#333" style={styles.optionIcon} />
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>Blank Form — PDF</Text>
                <Text style={styles.optionDescription}>
                  Empty form for handwriting
                </Text>
              </View>
            </TouchableOpacity>

            <View style={styles.optionSeparator} />

            <TouchableOpacity
              style={styles.modalOption}
              onPress={() => handleBlankSelect('docx')}
              testID="pdf-option-blank-word"
            >
              <FileText size={20} color="#333" style={styles.optionIcon} />
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>Blank Form — Word</Text>
                <Text style={styles.optionDescription}>
                  Empty form as an editable .docx
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <PDFViewerModal
        visible={pdfViewerVisible}
        onClose={handleClosePdfViewer}
        source={pdfSource}
        downloadFileName={pdfFileName}
      />

      <ImageSelectionModal
        visible={imageModalVisible}
        images={imageAttachments}
        onCancel={handleImageSelectionCancel}
        onConfirm={handleImageSelectionConfirm}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  triggerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ff6b35',
    paddingHorizontal: Platform.OS === 'web' ? 18 : 6,
    paddingVertical: Platform.OS === 'web' ? 0 : 6,
    minHeight: Platform.OS === 'web' ? 36 : undefined,
    // Shared with Location Tags, View Devices, Version History and Discussion.
    borderRadius:
      Platform.OS === 'web'
        ? spacing.toolbarActionBorderRadius
        : spacing.toolbarActionBorderRadiusNative,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        ':hover': {
          backgroundColor: '#e55a2b',
        },
      },
    }),
  },
  disabledButton: {
    backgroundColor: '#ccc',
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
        ':hover': {
          backgroundColor: '#ccc',
        },
      },
    }),
  },
  buttonText: {
    color: '#fff',
    fontSize: Platform.OS === 'web' ? 14 : 11,
    fontWeight: '500',
    marginLeft: Platform.OS === 'web' ? 6 : 4,
    marginRight: Platform.OS === 'web' ? 4 : 2,
  },
  disabledText: {
    color: '#999',
  },
  icon: {
    marginRight: 2,
  },
  chevron: {
    marginLeft: 2,
  },
  dropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        ':hover': {
          backgroundColor: '#f8f9fa',
        },
      },
    }),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    paddingVertical: 20,
    marginHorizontal: 20,
    minWidth: 300,
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center',
    marginBottom: 20,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  optionIcon: {
    marginRight: 12,
  },
  optionTextContainer: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 13,
    color: '#666',
  },
  optionSeparator: {
    height: 1,
    backgroundColor: '#e0e0e0',
    marginHorizontal: 20,
  },
});
